/**
 * pwa/update-prompt.ts
 *
 * Decides when to offer "a new version is ready". The service worker reports
 * a waiting update; the prompt only appears while no pointer is down, so it
 * never pops up (or moves the layout) in the middle of a stroke. Reloading
 * is the player's choice: game pages save on `pagehide`.
 */

export type UpdateListener = (visible: boolean) => void;

/** Looks for a new version: resolves true when one was found (it is on its way to `updateReady`). */
export type UpdateChecker = () => Promise<boolean>;

/** What looking for a new version on demand found. */
export type UpdateCheckResult =
  /** A new version is waiting: `apply` moves to it. */
  | 'ready'
  /** This is the newest version. */
  | 'none'
  /** Nothing to ask (no service worker: development). */
  | 'unavailable'
  /** The check failed (offline, or the server didn't answer). */
  | 'failed';

export class UpdatePromptController {
  private _applyUpdate: (() => Promise<void>) | null = null;
  /** Asks the service worker to look for a new version now (none without one: development). */
  private _check: UpdateChecker | null = null;
  private readonly _readyWaiters = new Set<() => void>();
  private _dismissed = false;
  private readonly _pointers = new Set<number>();
  private readonly _listeners = new Set<UpdateListener>();
  private _visible = false;

  constructor(private readonly _target: EventTarget = globalThis) {
    _target.addEventListener('pointerdown', this._onDown, { capture: true });
    _target.addEventListener('pointerup', this._onUp, { capture: true });
    _target.addEventListener('pointercancel', this._onUp, { capture: true });
    // A pointerup can be lost (released outside the window); forget pressed
    // pointers when the window loses focus so the prompt can't get stuck.
    _target.addEventListener('blur', this._onReset);
  }

  /** The service worker has a new version waiting; `apply` activates it and reloads. */
  updateReady(apply: () => Promise<void>): void {
    this._applyUpdate = apply;
    this._dismissed = false;
    this._sync();
    for (const waiter of this._readyWaiters) waiter();
  }

  /** How to look for a new version on demand (the service worker's registration sets it). */
  setChecker(check: UpdateChecker): void {
    this._check = check;
  }

  /**
   * Looks for a new version now (the About page's Check for updates). A new
   * version found is waited for until it is ready, up to `timeoutMs`; it
   * shows the update prompt too.
   */
  async checkForUpdate(timeoutMs = 15_000): Promise<UpdateCheckResult> {
    if (this._applyUpdate) return 'ready';
    if (!this._check) return 'unavailable';
    let found: boolean;
    try {
      found = await this._check();
    } catch {
      return 'failed';
    }
    // `updateReady` during the check has set `_applyUpdate` by now; a later one meets the waiter.
    if (found && !this._applyUpdate) await this._nextReady(timeoutMs);
    return this._applyUpdate ? 'ready' : 'none';
  }

  /** Resolves when `updateReady` is next called, or after `timeoutMs`. */
  private _nextReady(timeoutMs: number): Promise<void> {
    return new Promise<void>((resolve) => {
      const done = () => {
        this._readyWaiters.delete(done);
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(done, timeoutMs);
      this._readyWaiters.add(done);
    });
  }

  /**
   * Moves to the newest version now, because this one can't go on (a room
   * said the app is too old): the waiting update, else one looked for now,
   * else a plain reload (without a service worker the page loads the
   * newest files). The About page's Update now and Reload use it too.
   */
  async updateNow(reload: () => void = () => location.reload(), timeoutMs = 15_000): Promise<void> {
    if (await this.checkForUpdate(timeoutMs) === 'ready') await this.apply();
    else reload();
  }

  get visible(): boolean {
    return this._visible;
  }

  subscribe(listener: UpdateListener): () => void {
    this._listeners.add(listener);
    listener(this._visible);
    return () => this._listeners.delete(listener);
  }

  /** Reload into the new version. */
  async apply(): Promise<void> {
    const apply = this._applyUpdate;
    if (!apply) return;
    this._applyUpdate = null;
    this._sync();
    await apply();
  }

  /**
   * Hide the prompt. The new version still waits: it takes over once every
   * tab of the app has closed, or when the prompt is accepted later.
   */
  dismiss(): void {
    this._dismissed = true;
    this._sync();
  }

  destroy(): void {
    this._target.removeEventListener('pointerdown', this._onDown, { capture: true });
    this._target.removeEventListener('pointerup', this._onUp, { capture: true });
    this._target.removeEventListener('pointercancel', this._onUp, { capture: true });
    this._target.removeEventListener('blur', this._onReset);
    this._listeners.clear();
  }

  private readonly _onDown = (event: Event) => {
    this._pointers.add((event as PointerEvent).pointerId ?? 0);
  };

  private readonly _onUp = (event: Event) => {
    this._pointers.delete((event as PointerEvent).pointerId ?? 0);
    this._sync();
  };

  private readonly _onReset = () => {
    this._pointers.clear();
    this._sync();
  };

  private _sync(): void {
    const wanted = this._applyUpdate !== null && !this._dismissed;
    // Appear only between gestures; once shown, stay until acted on.
    const visible = wanted && (this._visible || this._pointers.size === 0);
    if (visible === this._visible) return;
    this._visible = visible;
    for (const listener of this._listeners) listener(visible);
  }
}

/** The app's one controller; `register-sw` feeds it, the banner reads it. */
export const updatePrompt = new UpdatePromptController();
